import { useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Database,
  Info,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import "../about-rbim.css";

type InfoRow = {
  label: string;
  value: string;
};

export function AboutRbimPage() {
  const [openFaqIds, setOpenFaqIds] = useState<string[]>(["what-is-rbim"]);

  function toggleFaq(faqId: string) {
    setOpenFaqIds((current) =>
      current.includes(faqId)
        ? current.filter((id) => id !== faqId)
        : [...current, faqId],
    );
  }

  return (
    <section className="portal-page about-rbim-page">
      <header className="about-rbim-desktop-header">
        <div className="about-rbim-header-main">
          <span className="about-rbim-header-icon" aria-hidden="true">
            <Info />
          </span>
          <div>
            <h1>About RBIM</h1>
            <p>Learn what RBIM is and how it helps barangay records management.</p>
          </div>
        </div>
        <span className="about-rbim-header-chip">
          <Database aria-hidden="true" />
          Offline-first barangay records system
        </span>
      </header>

      <AboutCard
        title="System Overview"
        description="A simple summary of the RBIM system, its users, and the records it manages."
      >
        <p className="about-rbim-overview-text">
          RBIM, or Registry of Barangay Inhabitants and Migrants, is a barangay records management system designed
          to help barangay staff organize household profiles, individual resident information, migration records,
          and certificate-related records in one system.
        </p>

        <div className="about-rbim-info-grid">
          {ABOUT_RBIM_DETAILS.map((item) => (
            <InfoBlock key={item.label} label={item.label} value={item.value} />
          ))}
        </div>

        <InfoList title="Data Managed" items={RBIM_DATA_MANAGED} />
      </AboutCard>

      <AboutCard
        title="Key Features"
        description="Core tools available to authorized barangay records staff."
      >
        <ul className="about-rbim-feature-grid">
          {RBIM_KEY_FEATURES.map((feature) => (
            <li key={feature} className="about-rbim-feature-item">
              <CheckCircle2 aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </AboutCard>

      <AboutCard
        title="Frequently Asked Questions"
        description="Quick answers to common questions about using RBIM."
      >
        <div className="about-rbim-faq">
          {FAQ_ITEMS.map((item) => {
            const isOpen = openFaqIds.includes(item.id);
            const answerId = `about-rbim-faq-answer-${item.id}`;

            return (
              <div
                key={item.id}
                className={`about-rbim-faq-item${isOpen ? " is-open" : ""}`}
              >
                <button
                  type="button"
                  className="about-rbim-faq-button"
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => toggleFaq(item.id)}
                >
                  <span>{item.question}</span>
                  <ChevronDown aria-hidden="true" />
                </button>
                {isOpen ? (
                  <div id={answerId} className="about-rbim-faq-answer">
                    {item.answer}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </AboutCard>

      <AboutCard
        title="System / Technical Requirements"
        description="Recommended device, browser, and internet requirements for accessing the RBIM Web Portal."
      >
        <div className="about-rbim-requirements-grid">
          <RequirementCard badge="Minimum" title="Minimum Requirements" rows={MINIMUM_REQUIREMENTS} />
          <RequirementCard badge="Recommended" title="Recommended Requirements" rows={RECOMMENDED_REQUIREMENTS} />
        </div>
      </AboutCard>

      <AboutCard
        title="System Build Information"
        description="Technical details for the current RBIM Web Portal."
      >
        <RequirementCard badge="System Build" title="System Build Information" rows={SYSTEM_BUILD_INFORMATION} />
      </AboutCard>

      <section className="about-rbim-security">
        <span className="about-rbim-security-icon" aria-hidden="true">
          <ShieldCheck />
        </span>
        <div>
          <h2>Security Reminder</h2>
          <p>
            Because RBIM stores barangay records locally, always keep backup files in a safe location. Avoid sharing
            the database or backup files with unauthorized persons.
          </p>
        </div>
      </section>
    </section>
  );
}

const ABOUT_RBIM_DETAILS = [
  {
    label: "System Name",
    value: "Registry of Barangay Inhabitants and Migrants",
  },
  {
    label: "Short Name",
    value: "RBIM",
  },
  {
    label: "Purpose",
    value:
      "To help barangay staff maintain accurate household and individual resident records, monitor in-migration and out-migration, and generate barangay-related documents more efficiently.",
  },
  {
    label: "Main Users",
    value: "Barangay staff, barangay records personnel, and authorized local government users.",
  },
  {
    label: "System Type",
    value: "Offline-first desktop barangay records system.",
  },
] satisfies InfoRow[];

const RBIM_DATA_MANAGED = [
  "Household profiles",
  "Individual resident profiles",
  "Migration details",
  "Address and barangay location records",
  "Certificate-related information",
  "Backup and restore files",
];

const RBIM_KEY_FEATURES = [
  "Household profile management",
  "Individual profile management",
  "Household member listing",
  "Migration record tracking",
  "Certificate generation",
  "Import and export tools",
  "Backup and restore",
  "Location lock",
  "Account security",
  "Local database storage",
];

const FAQ_ITEMS = [
  {
    id: "what-is-rbim",
    question: "What is RBIM?",
    answer:
      "RBIM stands for Registry of Barangay Inhabitants and Migrants. It is a barangay records management system used to organize household records, individual resident profiles, migration information, and certificate-related records.",
  },
  {
    id: "offline-use",
    question: "Does the RBIM Web Portal require internet?",
    answer:
      "Yes. The RBIM Web Portal is an online-based system and requires an internet connection to access, encode, view, update, and synchronize records with the central RBIM server.",
  },
  {
    id: "where-records-stored",
    question: "Where are records encoded in the Web Portal stored?",
    answer:
      "Records encoded through the RBIM Web Portal are saved directly to the municipality's central RBIM server or cloud database. Because the Web Portal is online-based, submitted records are stored centrally instead of only on the user's computer.",
  },
  {
    id: "why-lock-location",
    question: "Why do I need to lock the location?",
    answer:
      "The location lock helps make sure that records are assigned to the correct barangay location. This prevents accidental encoding under the wrong location.",
  },
  {
    id: "change-location-after-lock",
    question: "Can the location be changed after locking?",
    answer:
      "Changing the locked location may require verification or administrative action, depending on the system rules. This is done to protect the accuracy of existing records.",
  },
  {
    id: "backup-restore-purpose",
    question: "What is the purpose of Backup and Restore?",
    answer:
      "Backup and Restore helps protect barangay records from accidental deletion, device failure, or system reinstallation. A backup file can be used to recover saved records.",
  },
  {
    id: "generate-certificates",
    question: "Can RBIM generate certificates?",
    answer:
      "Yes. RBIM includes certificate-related features that allow barangay staff to generate documents using saved resident information.",
  },
  {
    id: "password-verification",
    question: "Why is password verification required?",
    answer:
      "Password verification helps protect important actions such as changing settings, restoring backups, deleting records, or accessing sensitive information.",
  },
  {
    id: "multiple-users",
    question: "Can multiple users use the same RBIM system?",
    answer:
      "RBIM can be used by authorized barangay staff on the installed device. User access depends on the account and security setup configured in the system.",
  },
  {
    id: "before-update-reinstall",
    question: "What should I do before updating or reinstalling RBIM?",
    answer:
      "Always create a backup first. Save the backup file in a safe location such as an external drive, USB drive, or secure cloud storage before updating or reinstalling the system.",
  },
];

const MINIMUM_REQUIREMENTS = [
  { label: "Operating System", value: "Windows 10, Windows 11, macOS, or supported Linux distribution" },
  { label: "Browser", value: "Latest version of Microsoft Edge, Google Chrome, or Mozilla Firefox" },
  { label: "Processor", value: "Dual-core processor or equivalent" },
  { label: "Memory", value: "4 GB RAM" },
  { label: "Display", value: "1366 x 768 screen resolution or higher" },
  { label: "Internet", value: "Required" },
  { label: "Connection", value: "Stable broadband or mobile data connection" },
  { label: "User Access", value: "Authorized RBIM Web Portal account" },
] satisfies InfoRow[];

const RECOMMENDED_REQUIREMENTS = [
  { label: "Operating System", value: "Windows 10/11, macOS, or supported Linux distribution" },
  { label: "Browser", value: "Latest version of Microsoft Edge or Google Chrome" },
  { label: "Processor", value: "Intel Core i5, AMD Ryzen 5, or equivalent" },
  { label: "Memory", value: "8 GB RAM or higher" },
  { label: "Display", value: "1920 x 1080 screen resolution or higher" },
  { label: "Internet", value: "Stable broadband connection" },
  { label: "Recommended Connection Speed", value: "At least 10 Mbps" },
  { label: "Security", value: "Updated browser and operating system" },
  { label: "User Access", value: "Authorized account with the correct Municipality/City or Barangay role" },
] satisfies InfoRow[];

const SYSTEM_BUILD_INFORMATION = [
  { label: "Application Type", value: "Online municipality/city web application" },
  { label: "Frontend", value: "React with TypeScript" },
  { label: "Build Tool", value: "Vite" },
  { label: "Backend", value: "Node.js API server" },
  { label: "Database", value: "PostgreSQL" },
  { label: "UI Styling", value: "Web portal component styling and responsive CSS" },
  { label: "Data Storage", value: "Central municipality server or cloud-hosted PostgreSQL database" },
  { label: "Access Control", value: "Role-based access with approved Municipality/City and Barangay scope" },
  { label: "Deployment", value: "Municipality server or cloud server accessed through a web browser" },
  { label: "Internet", value: "Required for normal Web Portal operation" },
] satisfies InfoRow[];

function AboutCard({
  children,
  description,
  title,
}: {
  children: React.ReactNode;
  description: string;
  title: string;
}) {
  return (
    <section className="about-rbim-card">
      <header className="about-rbim-card-header">
        <h2>{title}</h2>
        <p>{description}</p>
      </header>
      <div className="about-rbim-card-content">{children}</div>
    </section>
  );
}

function InfoBlock({ label, value }: InfoRow) {
  return (
    <div className="about-rbim-info-block">
      <p className="about-rbim-info-label">{label}</p>
      <p className="about-rbim-info-value">{value}</p>
    </div>
  );
}

function InfoList({ items, title }: { items: string[]; title: string }) {
  return (
    <section className="about-rbim-list-box">
      <h3>{title}</h3>
      <ul className="about-rbim-data-list">
        {items.map((item) => (
          <li key={item}>
            <span className="about-rbim-bullet" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RequirementCard({
  badge,
  rows,
  title,
}: {
  badge: string;
  rows: InfoRow[];
  title: string;
}) {
  const BadgeIcon = badge === "Recommended" ? CheckCircle2 : LockKeyhole;

  return (
    <section className="about-rbim-requirement-card">
      <div className="about-rbim-requirement-title">
        <span className="about-rbim-badge">
          <BadgeIcon aria-hidden="true" />
          {badge}
        </span>
        <h3>{title}</h3>
      </div>
      <div className="about-rbim-requirement-rows">
        {rows.map((row) => (
          <div key={row.label} className="about-rbim-requirement-row">
            <strong>{row.label}</strong>
            <span>{row.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
